import { forwardRef, useId } from "react";

import peekSrc from "@/assets/brand/robot/robot-peek-blank.webp";
import { cn } from "@/lib/utils";

const W = 589;
const H = 288;
const VIEW = `0 0 ${W} ${H}`;

const EYES = [
  { cx: 224, cy: 157 },
  { cx: 352.5, cy: 158.5 }
] as const;

/**
 * The ledge he grips: the render is cut at `y`, just under his chin, and a
 * hand rests on it either side of his head. The right one is the hand that
 * waves, so it lets go while the arm is up.
 */
const LEDGE = { y: 236, grips: [37, 471], width: 81 } as const;
const LEDGE_CUT = `${((H - LEDGE.y) / H) * 100}%`;

const gripPath = (x: number) => {
  const w = LEDGE.width;
  const top = LEDGE.y - 29;
  const bottom = LEDGE.y + 23;
  return `M${x} ${top + 28} Q${x} ${top} ${x + 28} ${top} H${x + w - 28} Q${
    x + w
  } ${top} ${x + w} ${top + 28} V${bottom - 14} Q${x + w} ${bottom} ${
    x + w - 14
  } ${bottom} H${x + 14} Q${x} ${bottom} ${x} ${bottom - 14} Z`;
};

type Props = {
  /** Smiling with the hand down, no loop. */
  still?: boolean;
  /**
   * Sinks him to the chin behind whatever sits below him (a title), with both
   * hands on its top edge. His box ends at the ledge; the hands hang just
   * past it.
   */
  ledge?: boolean;
  className?: string;
};

/**
 * The landing site's robot (its hero callout `HeroPeek`), cut out on a
 * transparent background so he peeks over whatever he sits on.
 * The render carries his head, shoulders and cheeks with the face left blank;
 * the eyes, mouth and a waving hand are drawn over it on the render's pixel
 * grid. The hand sits behind the render and rises from behind his right
 * shoulder. Size him by width or height; the other follows the render.
 * Timing lives with the `agent-peek` rules in styles/motion.css.
 */
export const AgentPeek = forwardRef<HTMLSpanElement, Props>(
  ({ still = false, ledge = false, className }, ref) => {
    const arm = `agent-peek-arm-${useId().replace(/:/g, "")}`;
    return (
      <span
        ref={ref}
        aria-hidden
        data-testid="agent-peek"
        data-still={still || undefined}
        data-ledge={ledge || undefined}
        style={{ aspectRatio: `${W} / ${ledge ? LEDGE.y : H}` }}
        className={cn("agent-peek relative block shrink-0", className)}
      >
        <span
          style={{ aspectRatio: `${W} / ${H}` }}
          className="absolute inset-x-0 top-0"
        >
          <svg
            viewBox={VIEW}
            style={
              ledge
                ? { clipPath: `inset(-100% -100% ${LEDGE_CUT} -100%)` }
                : undefined
            }
            className="agent-peek-back absolute inset-0 size-full overflow-visible"
          >
            <defs>
              <linearGradient id={arm} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" className="agent-peek-arm-light" />
                <stop offset="0.45" className="agent-peek-arm-mid" />
                <stop offset="1" className="agent-peek-arm-shade" />
              </linearGradient>
            </defs>
            <rect
              className="agent-peek-arm"
              x={485}
              y={112}
              width={90}
              height={150}
              rx={30}
              fill={`url(#${arm})`}
            />
          </svg>

          <img
            src={peekSrc}
            width={W}
            height={H}
            alt=""
            draggable={false}
            style={
              ledge ? { clipPath: `inset(0 0 ${LEDGE_CUT} 0)` } : undefined
            }
            className="relative block size-full"
          />

          <svg viewBox={VIEW} className="absolute inset-0 size-full">
            {EYES.map(({ cx, cy }) => (
              <ellipse
                key={`blush-${cx}`}
                className="agent-peek-blush"
                cx={cx - (cx < 300 ? 14 : -16)}
                cy={cy + 32}
                rx={24}
                ry={13}
              />
            ))}

            {EYES.map(({ cx, cy }) => (
              <g key={`eye-${cx}`} className="agent-peek-eye">
                <circle cx={cx} cy={cy} r={14.5} className="agent-peek-ink" />
                <circle
                  cx={cx - 4.5}
                  cy={cy - 6}
                  r={3.6}
                  className="agent-peek-glint"
                />
              </g>
            ))}
            {EYES.map(({ cx, cy }) => (
              <path
                key={`happy-${cx}`}
                className="agent-peek-happy"
                d={`M${cx - 14} ${cy + 5} Q${cx} ${cy - 15} ${cx + 14} ${
                  cy + 5
                }`}
                fill="none"
                strokeWidth={7}
                strokeLinecap="round"
              />
            ))}

            <path
              className="agent-peek-smile"
              d="M271 177 Q288 193 305 177"
              fill="none"
              strokeWidth={4.5}
              strokeLinecap="round"
            />
            <g className="agent-peek-grin">
              <path
                d="M265 174 H311 Q311 202 288 202 Q265 202 265 174 Z"
                strokeLinejoin="round"
                strokeWidth={3}
                className="agent-peek-mouth"
              />
              <ellipse
                cx={288}
                cy={196}
                rx={11}
                ry={5.5}
                className="agent-peek-tongue"
              />
            </g>
          </svg>
        </span>

        {ledge ? (
          <svg
            viewBox={`0 0 ${W} ${LEDGE.y}`}
            className="absolute inset-0 size-full overflow-visible"
          >
            {LEDGE.grips.map((x, i) => (
              <g
                key={x}
                className={i === 1 ? "agent-peek-grip-wave" : undefined}
              >
                <path d={gripPath(x)} fill={`url(#${arm})`} />
                <path
                  d={`M${x + 16} ${LEDGE.y - 3} H${x + LEDGE.width - 16}`}
                  className="agent-peek-knuckle"
                  strokeWidth={4}
                  strokeLinecap="round"
                />
              </g>
            ))}
          </svg>
        ) : null}
      </span>
    );
  }
);
AgentPeek.displayName = "AgentPeek";
